import React, {ReactNode} from "react";
import PortalDialogType from "../../../../dialog/PortalDialogType";
import Parameter from "../../../../dialog/parameter/Parameter";
import PortalDocument from "../../../PortalDocument";
import MimeTypeFilter from "../../../mimetype/MimeTypeFilter";
import MimeTypes from "../../../mimetype/MimeTypes";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {ColorPicker} from "primereact/colorpicker";
import PortalDocumentManager from "../../../PortalDocumentManager";
import DialogDefault from "../../../../dialog/parameter/PortalDialogDefault";
import validator from "validator";
import MimeType from "../../../mimetype/MimeType";
import Message from "../../../../../shared/log/Message";
import {MessageType} from "../../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Blacken,
	Functions,
	Information,
	Security,
	Settings,
	SignatureEditProfile,
	ToolboxRedactRedact
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import PortalDialogSecurity, {Intention} from "../../../../dialog/PortalDialogSecurity";
import Translate from "../../../../../shared/translation/Translate";
import SidebarFunction, {SidebarFunctionProperties, SidebarFunctionState} from "../SidebarFunction";
import Immutable from "../../../../../shared/immutable/Immutable";
import DocumentSettings from "../../../DocumentSettings";
import RedactDialogFields from "../../../../dialog/field/redact/RedactDialogFields";
import FunctionHelper from "../../../../../shared/helper/FunctionHelper";
import {PortalToolboxAnnotationAnnotation} from "../../../../../shared/models/PortalToolboxAnnotationAnnotation";
import {InputSwitch} from "primereact/inputswitch";
import {PortalRedactAnnotation} from "../../../../../shared/models/PortalRedactAnnotation";
import Info from "../../../../../shared/components/Info";
import {InputNumber} from "primereact/inputnumber";
import {Slider} from "primereact/slider";
import PortalDialogDefault from "../../../../dialog/parameter/PortalDialogDefault";
import MessageBox from "../../../../../shared/components/MessageBox";

export interface RedactAreaFunctionStatus extends SidebarFunctionState<PortalToolboxAnnotationAnnotation> {
	applyParameter?: Parameter<ToolboxRedactRedact>;
}

export class RedactAreaFunctionDefinition extends SidebarFunction<PortalToolboxAnnotationAnnotation,
	SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>, RedactAreaFunctionStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<RedactDialogFields, string>(),
			applyParameter: DialogDefault.getParameter(
				PortalDialogType.REDACT, this.props.context.documentManager.getSelection()
			)
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.redact, Functions.getRedactDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.REDACT_AREA, signatureEditProfile, Intention.editingContent, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable()) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderContents(): ReactNode {
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;
		let applyParameter: ToolboxRedactRedact | undefined = this.state.applyParameter?.data;
		if (!FunctionHelper.exists(parameter?.add?.redact) || !FunctionHelper.exists(applyParameter)) {
			return null;
		}

		let annotationParameter: PortalRedactAnnotation | undefined = parameter!.add!.redact![0];
		if (!FunctionHelper.exists(annotationParameter?.position?.pathElement)) {
			return null;
		}

		let applyRedaction: boolean = FunctionHelper.applyValue(
			annotationParameter.apply, PortalRedactAnnotation.getApplyDefault()
		);

		return (
			<>
				<div className="function-content">
					{this.renderError()}
					<fieldset className="p-col-12 p-grid">
						<legend>
							<Translate {...this.props} data="redact.properties" namespace="dialog"/>
						</legend>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.CREATOR)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-creator">
								<Translate {...this.props} data="redact.content.creator.label"
										   namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-creator"
										   className={errors.has(RedactDialogFields.CREATOR) ? "p-invalid" : ""}
										   tooltip={errors.has(RedactDialogFields.CREATOR) ? Translate.value(
											   errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
										   ) : ""}
										   value={FunctionHelper.applyValue(
											   annotationParameter?.creator, PortalRedactAnnotation.getCreatorDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.CREATOR, e.currentTarget.value
										   )}/>
								{this.renderFieldError(errors.has(RedactDialogFields.CREATOR) ? Translate.value(
									errors.get(RedactDialogFields.CREATOR)!, {ns: "message"}
								) : "")}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.SUBJECT)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-subject">
								<Translate {...this.props} data="redact.content.subject.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-subject"
										   value={FunctionHelper.applyValue(
											   annotationParameter?.subject, PortalRedactAnnotation.getSubjectDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.SUBJECT, e.currentTarget.value
										   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.CONTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-text">
								<Translate {...this.props} data="redact.content.text.label" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputTextarea rows={2} id="control-text"
											   value={FunctionHelper.applyValue(
												   annotationParameter?.contents, PortalRedactAnnotation.getContentsDefault()
											   )}
											   onInput={(e) => this.onChange(
												   RedactDialogFields.CONTENTS, e.currentTarget.value
											   )}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.INTENTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-intents">
								<Translate {...this.props} data="redact.content.intents" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8 p-grid">
								<InputText id="control-intents"
										   value={FunctionHelper.applyValue(
											   annotationParameter?.intents, PortalRedactAnnotation.getIntentsDefault()
										   )}
										   onInput={(e) => this.onChange(
											   RedactDialogFields.INTENTS, e.currentTarget.value
										   )}/>
							</div>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid">
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.BORDER)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-color-outline">
								<Translate {...this.props} data="redact.appearance.color.outline" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className={"p-inputgroup" + (
									errors.has(RedactDialogFields.BORDER) ? " p-invalid" : ""
								)}>
									<InputText
										className={errors.has(RedactDialogFields.BORDER) ? "p-invalid" : ""}
										tooltip={errors.has(RedactDialogFields.BORDER) ? Translate.value(
											errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
										) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter?.color, PortalRedactAnnotation.getColorDefault()
										)}
										onInput={(e) => this.onChange(
											RedactDialogFields.BORDER, e.currentTarget.value
										)}/>
									<ColorPicker inputId="control-color-outline" className="p-inputgroup-addon"
												 format="hex"
												 defaultColor={PortalRedactAnnotation.getColorDefault().replace(
													 "#", ""
												 )}
												 value={FunctionHelper.applyValue(
													 annotationParameter?.color, PortalRedactAnnotation.getColorDefault()
												 ).replace("#", "")}
												 onChange={(e) => this.onChange(
													 RedactDialogFields.BORDER, "#" + e.value
												 )}
												 appendTo={document.getElementById("root")}/>
								</div>
								{this.renderFieldError(errors.has(RedactDialogFields.BORDER) ? Translate.value(
									errors.get(RedactDialogFields.BORDER)!, {ns: "message"}
								) : "")}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.FILL)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-color-interior">
								<Translate {...this.props} data="redact.appearance.color.interior" namespace="dialog"/>
							</label>
							<div className="p-col-12 p-sm-8">
								<div className="p-inputgroup">
									<InputText
										className={errors.has(RedactDialogFields.FILL) ? "p-invalid" : ""}
										tooltip={errors.has(RedactDialogFields.FILL) ? Translate.value(
											errors.get(RedactDialogFields.FILL)!, {ns: "message"}
										) : ""}
										value={FunctionHelper.applyValue(
											annotationParameter?.interiorColor, PortalRedactAnnotation.getInteriorColorDefault()
										)}
										onInput={(e) => this.onChange(
											RedactDialogFields.FILL, e.currentTarget.value
										)}/>
									<ColorPicker className="p-inputgroup-addon" format="hex"
												 defaultColor={PortalRedactAnnotation.getInteriorColorDefault().replace(
													 "#", ""
												 )}
												 value={FunctionHelper.applyValue(
													 annotationParameter.interiorColor,
													 PortalRedactAnnotation.getInteriorColorDefault()
												 )?.replace("#", "")}
												 inputId="control-color-interior"
												 onChange={(e) => this.onChange(
													 RedactDialogFields.FILL, "#" + e.value
												 )} appendTo={document.getElementById("root")}/>
								</div>
								{this.renderFieldError(errors.has(RedactDialogFields.FILL) ? Translate.value(
									errors.get(RedactDialogFields.FILL)!, {ns: "message"}
								) : "")}
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.APPLY)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-apply"></label>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-apply" checked={applyRedaction}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.APPLY, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-apply">
									<Translate {...this.props} data="redact.apply.label" namespace="dialog"/>
								</label>
							</div>
						</div>
					</fieldset>
					<fieldset className="p-col-12 p-grid" hidden={!applyRedaction}>
						<legend>
							<Translate {...this.props} data="redact.advanced" namespace="dialog"/>
						</legend>
						<div className="p-col-12" hidden={!applyRedaction}>
							<MessageBox type={MessageType.INFO} language={this.props.language} closeable={true}>
								<Translate {...this.props} data="redact.apply.info" namespace="dialog"/>
							</MessageBox>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.COLLISION)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-collision">
								<Translate {...this.props} data="redact.apply.properties.collision.label"
										   namespace="dialog"/>
								<Info {...this.props} data="redact.apply.properties.collision.description"
									  options={{ns: "dialog"}}/>
							</label>
							<div className="p-col-12 p-sm-8 slider-input">
								<InputNumber inputId="control-collision" step={1} suffix=" %" showButtons={true}
											 useGrouping={false}
											 value={FunctionHelper.applyValue(
												 applyParameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
											 )}
											 min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()}
											 onValueChange={(e) => this.onChange(
												 RedactDialogFields.COLLISION, e.value
											 )}/>
								<Slider step={1}
										value={FunctionHelper.applyValue(
											applyParameter?.blacken?.collisionRatio, Blacken.getCollisionRatioDefault()
										)}
										min={Blacken.getCollisionRatioMin()} max={Blacken.getCollisionRatioMax()}
										onChange={(e) => this.onChange(
											RedactDialogFields.COLLISION, e.value as number
										)}/>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.ANNOTATIONS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-annotations"/>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-annotations"
											 checked={FunctionHelper.applyValue(
												 applyParameter?.blacken?.redactAnnotations,
												 Blacken.getRedactAnnotationsDefault()
											 )}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.ANNOTATIONS, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-annotations">
									<Translate {...this.props} data="redact.apply.properties.annotations.label"
											   namespace="dialog"/>
									<Info {...this.props} data="redact.apply.properties.annotations.description"
										  options={{ns: "dialog"}}/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.IMAGES)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-images"/>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-images"
											 checked={FunctionHelper.applyValue(
												 applyParameter?.blacken?.redactImages, Blacken.getRedactImagesDefault()
											 )}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.IMAGES, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-images">
									<Translate {...this.props} data="redact.apply.properties.images.label"
											   namespace="dialog"/>
									<Info {...this.props} data="redact.apply.properties.images.description"
										  options={{ns: "dialog"}}/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.PATHES)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-pathes"/>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-pathes"
											 checked={FunctionHelper.applyValue(
												 applyParameter?.blacken?.redactPaths, Blacken.getRedactPathsDefault()
											 )}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.PATHES, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-pathes">
									<Translate {...this.props} data="redact.apply.properties.pathes.label"
											   namespace="dialog"/>
									<Info {...this.props} data="redact.apply.properties.pathes.description"
										  options={{ns: "dialog"}}/>
								</label>
							</div>
						</div>
						<div className="p-col-12 p-grid"
							 hidden={!this.state.fields?.contains(RedactDialogFields.TEXTS)}>
							<label className="p-col-12 p-sm-4" htmlFor="control-texts"/>
							<div className="p-col-12 p-sm-8 switch-container">
								<InputSwitch inputId="control-texts"
											 checked={FunctionHelper.applyValue(
												 applyParameter?.blacken?.redactTexts, Blacken.getRedactTextsDefault()
											 )}
											 onChange={(e) => this.onChange(
												 RedactDialogFields.TEXTS, e.value
											 )}/>
								<label className="switch-label" htmlFor="control-texts">
									<Translate {...this.props} data="redact.apply.properties.texts.label"
											   namespace="dialog"/>
									<Info {...this.props} data="redact.apply.properties.texts.description"
										  options={{ns: "dialog"}}/>
								</label>
							</div>
						</div>
					</fieldset>
				</div>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["sidebar-function", "redact-area-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<>
				{this.props.visible &&
					<div className={classList.join(" ")}>
						{this.renderHeader()}
						{this.renderContents()}
						{this.renderFooter()}
					</div>
				}
			</>
		);
	}

	protected onChange(field: RedactDialogFields, value: any): void {
		let parameter: PortalToolboxAnnotationAnnotation = this.state.parameter!.data;
		let annotationParameter: PortalRedactAnnotation = parameter.add!.redact![0];
		let applyParameter: ToolboxRedactRedact = this.state.applyParameter!.data;
		let errors: Map<RedactDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case RedactDialogFields.CREATOR:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.creator = value;
				break;
			case RedactDialogFields.SUBJECT:
				annotationParameter.subject = value;
				break;
			case RedactDialogFields.CONTENTS:
				annotationParameter.contents = value;
				break;
			case RedactDialogFields.INTENTS:
				annotationParameter.intents = value;
				break;
			case RedactDialogFields.FILL:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.interiorColor = value;
				break;
			case RedactDialogFields.BORDER:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!validator.isHexColor(value)) {
					errors.set(field, "errors.fields.color");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				annotationParameter.color = value;
				break;
			case RedactDialogFields.APPLY:
				annotationParameter.apply = value;
				break;
			case RedactDialogFields.COLLISION:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					value = Blacken.getCollisionRatioDefault();
				}

				applyParameter.blacken!.collisionRatio = value;
				break;
			case RedactDialogFields.ANNOTATIONS:
				applyParameter.blacken!.redactAnnotations = value;
				break;
			case RedactDialogFields.IMAGES:
				applyParameter.blacken!.redactImages = value;
				break;
			case RedactDialogFields.PATHES:
				applyParameter.blacken!.redactPaths = value;
				break;
			case RedactDialogFields.TEXTS:
				applyParameter.blacken!.redactTexts = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter,
			applyParameter: this.state.applyParameter
		});

		this.props.context.getPage().setState({
			mouseFunctionAnnotationParameter: Immutable.from(this.state.parameter?.data.toJson()),
		});
	}

	protected renderHeader(): ReactNode {
		return null;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canModifyAnnotations, Security.getCanModifyAnnotationsDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxAnnotationAnnotation | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add?.redact)) {
			return;
		}

		let annotationParameter: PortalRedactAnnotation | undefined = parameter!.add!.redact![0];

		if (annotationParameter.creator === "") {
			annotationParameter.creator = Translate.value("redact.content.creator.default", {ns: "dialog"});
		}

		if (annotationParameter.subject === "") {
			annotationParameter.subject = Translate.value("redact.content.subject.default", {ns: "dialog"});
		}

		if (annotationParameter.contents === "") {
			annotationParameter.contents = Translate.value("redact.content.text.default", {ns: "dialog"});
		}

		this.setState({
			parameter: this.state.parameter,
			applyParameter: DialogDefault.getParameter(
				PortalDialogType.REDACT, this.props.context.documentManager.getSelection()
			)
		});
	};

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			let documentSettings: DocumentSettings = portalDocument.getSettings();
			documentSettings.showSidebarFunction = false;
		}

		documentManager.updateGlobalDocumentList();

		super.hide();
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();
		let parameter: PortalToolboxAnnotationAnnotation = this.state.parameter!.data.clone();
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let operationData: Array<BaseToolbox> = [
			BaseToolbox.fromJson({
				annotation: parameter.toJson()
			})
		];

		if (parameter.add?.redact[0].apply) {
			PortalDialogDefault.setParameter(PortalDialogType.REDACT, this.state.applyParameter!, selection);

			operationData.push(
				BaseToolbox.fromJson({
					redact: this.state.applyParameter!.data.toJson()
				})
			);
		}

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as Settings)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.redact.add", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.REDACT_AREA;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxAnnotationAnnotation> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const RedactAreaFunction = React.memo(RedactAreaFunctionDefinition,
	(
		prevProps: Readonly<SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>>,
		nextProps: Readonly<SidebarFunctionProperties<PortalToolboxAnnotationAnnotation>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default RedactAreaFunction;